import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  useApiAdmincompaniesIdGet,
  useApiAdmincompaniesGetCollection,
  getApiAdminusersGetCollectionQueryKey,
  getApiAdminusersIdGetQueryKey,
  useApiAdminusersIdGet,
  useApiAdminusersIdDelete,
  useApiUsersIdPatch,
  useSendMagicLink,
  useApiAdminusersIdhistoryGetCollection,
  getApiAdminusersIdhistoryGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type {
  CompanyCompanyCollectionReadModel,
  UserUserUpdateJsonMergePatchModel,
  UserWorkspaceUserReadModelStatus,
  UserWorkspaceUserUpdateModelMaxRole
} from '@r/api/generated/client.schemas.ts';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useEffect, useMemo, useState } from 'react';
import type { FormattedDataForRendering } from '@r/components/kpi/points/bar-chart-bloc.tsx';
import { getBarFillFromValue } from '@r/utils/chart-utils.ts';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useUser, useLogout } from '@r/api/auth.tsx';
import { getAssignableRoles, isAdminRole, roleToI18nKey, type Role } from '@r/utils/roles.ts';
import type { Option } from '@r/types/index.ts';
import { UserProfileHeader } from '@admin/components/user/user-profile-header.tsx';
import { UserDetailsGrid } from '@admin/components/user/user-details-grid.tsx';
import { UserSkeleton } from '@admin/components/user/user-skeleton.tsx';
import { UserLoginStatsCard } from '@admin/components/user/user-login-stats-card.tsx';
import { HistoryCard } from '@admin/components/history-card.tsx';
import { EmailHistoryTable } from '@admin/components/emails/email-history-table.tsx';
import { getEmailHistoryQueryKey } from '@admin/components/emails/email-history-query-key.ts';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { UserHistoryTable } from '@admin/pages/users/components/user-history-table.tsx';
import { Button } from '@r/components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@r/components/ui/dialog.tsx';
import { AlertTriangle, Trash2 } from 'lucide-react';

type UserStatus = UserWorkspaceUserReadModelStatus;
const COMMENT_MAX_LENGTH = 255;

const isAcceptedStatus = (status?: string | null) =>
  status === 'user.status.registered' || status === 'user.status.blocked';
const isBlockedStatus = (status?: string | null) => status === 'user.status.blocked';
const isInactiveStatus = (status?: string | null) => status === 'user.status.inactive';

export const User = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logout = useLogout();
  const currentUser = useUser();
  const { id: userId } = getRouteApi('/auth-layout/users/$id').useParams();

  const {
    data: user,
    isLoading,
    isSuccess,
    isError: isUserError,
    error: userError,
    errorUpdatedAt: userErrorUpdatedAt
  } = useApiAdminusersIdGet(userId);
  useOrvalQueryErrorToast(
    { isError: isUserError, error: userError, errorUpdatedAt: userErrorUpdatedAt },
    t('toast.error.label')
  );

  const workspace = getActiveWorkspaceUniversal(user?.userWorkspaces);
  const registeredAt = workspace?.registeredAt;
  const isRegistered = isAcceptedStatus(workspace?.status);
  const isBlocked = isBlockedStatus(workspace?.status);
  const isInactive = isInactiveStatus(workspace?.status);
  const [firstnameDraft, setFirstnameDraft] = useState('');
  const [lastnameDraft, setLastnameDraft] = useState('');
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [personalEmailDraft, setPersonalEmailDraft] = useState('');
  const [personalPhoneDraft, setPersonalPhoneDraft] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [comment2Draft, setComment2Draft] = useState('');
  const [comment3Draft, setComment3Draft] = useState('');
  const [uniqueCodeDraft, setUniqueCodeDraft] = useState('');
  const [programTermsAcceptedDraft, setProgramTermsAcceptedDraft] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<Option | null>(null);
  const [programRoleDraft, setProgramRoleDraft] =
    useState<UserWorkspaceUserUpdateModelMaxRole>('ROLE_MEMBER');
  const debouncedCompanySearch = useDebounce(companySearch, 500);
  const currentUserWorkspace = currentUser.data
    ? getActiveWorkspaceUniversal(currentUser.data.visibleWorkspaces)
    : null;
  const assignableRolesForAboutModal = useMemo(() => {
    const base = getAssignableRoles(currentUserWorkspace?.maxRole);
    if (!user?.id || !currentUser.data?.id) return base;
    const viewingSelf = String(currentUser.data.id) === String(user.id);
    if (!viewingSelf || !workspace?.maxRole) return base;
    const selfRole = workspace.maxRole as Role;
    return base.includes(selfRole) ? base : [...base, selfRole];
  }, [currentUser.data?.id, user?.id, currentUserWorkspace?.maxRole, workspace?.maxRole]);
  const isTermsReadOnly = currentUserWorkspace?.maxRole === 'ROLE_SALES_ADMIN';
  const canEditUniqueCode = currentUserWorkspace?.roles?.includes('ROLE_ADMIN') ?? false;
  const companyQuery = useApiAdmincompaniesGetCollection(
    { search: debouncedCompanySearch },
    { query: { enabled: debouncedCompanySearch.length > 0 } }
  );
  const companyId = workspace?.company?.id ? String(workspace.company.id) : null;
  const companyDetailsQuery = useApiAdmincompaniesIdGet(companyId);
  useOrvalQueryErrorToast(companyDetailsQuery, t('toast.error.label'));

  const historyFirstPageQuery = useApiAdminusersIdhistoryGetCollection(userId, {
    page: 1,
    perPage: 10
  });

  const hasHistory =
    historyFirstPageQuery.isLoading ||
    (historyFirstPageQuery.data?.data?.length ?? 0) > 0 ||
    !!registeredAt;

  const statusBeforeBlock = useMemo(() => {
    const history = (historyFirstPageQuery.data?.data ?? []) as Array<{
      property: string;
      previousValue: string;
      newValue: string;
    }>;
    const blockEntry = history.find(
      (entry) => entry.property === 'status' && entry.newValue === 'user.status.blocked'
    );
    return (blockEntry?.previousValue ??
      'user.status.unregistered') as UserWorkspaceUserReadModelStatus;
  }, [historyFirstPageQuery.data]);

  const blockContactMutation = useApiUsersIdPatch({
    mutation: {
      onSuccess: (_response, variables) => {
        const nextBlockedStatus = variables.data.userWorkspace?.status === 'user.status.blocked';
        toast.success(
          nextBlockedStatus
            ? t('admin.user.profile.toast.block.success')
            : t('admin.user.profile.toast.unblock.success')
        );
        queryClient.invalidateQueries({ queryKey: getApiAdminusersIdGetQueryKey(userId) });
        queryClient.invalidateQueries({ queryKey: getApiAdminusersGetCollectionQueryKey() });
        queryClient.invalidateQueries({
          queryKey: getApiAdminusersIdhistoryGetCollectionQueryKey(userId)
        });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.user.profile.toast.block.error'));
      }
    }
  });

  const updateAboutMutation = useApiUsersIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.user.profile.toast.about.success'));
        setIsAboutModalOpen(false);
        queryClient.invalidateQueries({ queryKey: getApiAdminusersIdGetQueryKey(userId) });
        queryClient.invalidateQueries({ queryKey: getApiAdminusersGetCollectionQueryKey() });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.user.profile.toast.about.error'));
      }
    }
  });

  const updateCompanyMutation = useApiUsersIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.user.profile.toast.company.success'));
        setIsCompanyModalOpen(false);
        queryClient.invalidateQueries({ queryKey: getApiAdminusersIdGetQueryKey(userId) });
        queryClient.invalidateQueries({ queryKey: getApiAdminusersGetCollectionQueryKey() });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.user.profile.toast.company.error'));
      }
    }
  });

  const isDeletingSelf = currentUser.data?.id != null && String(currentUser.data.id) === userId;

  const deleteMutation = useApiAdminusersIdDelete({
    mutation: {
      onSuccess: async () => {
        toast.success(t('admin.user.profile.toast.delete.success'));
        if (isDeletingSelf) {
          await logout();
          navigate({ to: '/login' });
          return;
        }
        queryClient.invalidateQueries({ queryKey: getApiAdminusersGetCollectionQueryKey() });
        navigate({ to: '/users' });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.user.profile.toast.delete.error'));
      }
    }
  });

  const sendMagicLinkMutation = useSendMagicLink({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.user.profile.toast.magic.link.success'));
        // Le lien de connexion part par e-mail : il doit apparaître dans l'historique.
        queryClient.invalidateQueries({
          queryKey: getEmailHistoryQueryKey({
            type: 'user',
            uniqueCode: workspace?.uniqueCode ?? undefined
          })
        });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.user.profile.toast.magic.link.error'));
      }
    }
  });

  const isLastManager = useMemo(() => {
    if (workspace?.maxRole !== 'ROLE_MANAGER') return false;
    const managerCount =
      companyDetailsQuery.data?.userWorkspaces.filter((uw) => uw.maxRole === 'ROLE_MANAGER')
        .length ?? 0;
    return managerCount === 1;
  }, [workspace?.maxRole, companyDetailsQuery.data?.userWorkspaces]);

  const hasSalesHierarchyDirectReports = (workspace?.salesHierarchyDirectReportsCount ?? 0) > 0;

  useEffect(() => {
    if (!user) return;
    setFirstnameDraft(user.firstname ?? '');
    setLastnameDraft(user.lastname ?? '');
    setPersonalEmailDraft(user.email ?? '');
    setPersonalPhoneDraft(workspace?.phone ?? '');
    setCommentDraft(workspace?.comment ?? '');
    setComment2Draft(workspace?.comment2 ?? '');
    setComment3Draft(workspace?.comment3 ?? '');
    setUniqueCodeDraft(workspace?.uniqueCode ?? '');
  }, [user, workspace]);

  useEffect(() => {
    if (!isAboutModalOpen || !user || !workspace) return;
    setFirstnameDraft(user.firstname ?? '');
    setLastnameDraft(user.lastname ?? '');
    setPersonalEmailDraft(user.email ?? '');
    setPersonalPhoneDraft(workspace?.phone ?? '');
    setCommentDraft(workspace?.comment ?? '');
    setComment2Draft(workspace?.comment2 ?? '');
    setComment3Draft(workspace?.comment3 ?? '');
    setUniqueCodeDraft(workspace?.uniqueCode ?? '');
    setProgramRoleDraft(
      (workspace.maxRole as UserWorkspaceUserUpdateModelMaxRole) ?? 'ROLE_MEMBER'
    );
    setProgramTermsAcceptedDraft(isAcceptedStatus(workspace.status));
  }, [isAboutModalOpen, user, workspace]);

  useEffect(() => {
    if (!isCompanyModalOpen) return;

    if (workspace?.company) {
      const option = {
        label: workspace.company.name,
        value: workspace.company.uniqueCode
      };
      setSelectedCompanyOption(option);
      setCompanySearch(option.label);
      return;
    }

    setSelectedCompanyOption(null);
    setCompanySearch('');
  }, [isCompanyModalOpen, workspace]);

  const handleToggleContactBlock = (nextBlockedStatus: boolean) => {
    if (!user || !workspace) return;

    const payload: UserUserUpdateJsonMergePatchModel = {
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname,
      userWorkspace: {
        uniqueCode: workspace.uniqueCode,
        companyUniqueCode: workspace.company?.uniqueCode ?? null,
        maxRole: workspace.maxRole as UserWorkspaceUserUpdateModelMaxRole,
        status: nextBlockedStatus ? 'user.status.blocked' : statusBeforeBlock,
        phone: workspace.phone ?? null,
        comment: workspace.comment ?? null,
        comment2: workspace.comment2 ?? null,
        comment3: workspace.comment3 ?? null
      }
    };

    blockContactMutation.mutate({ id: userId, data: payload });
  };

  const handleSubmitAboutModal = () => {
    if (!user || !workspace) return;

    const nextFirstname = firstnameDraft.trim();
    const nextLastname = lastnameDraft.trim();
    const nextEmail = personalEmailDraft.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!nextFirstname || !nextLastname || !nextEmail) {
      toast.error(t('admin.user.profile.validation.personal.required'));
      return;
    }
    if (!emailRegex.test(nextEmail)) {
      toast.error(t('form.field.invalid'));
      return;
    }
    if (
      nextFirstname.length > 50 ||
      nextLastname.length > 50 ||
      nextEmail.length > 255 ||
      commentDraft.trim().length > COMMENT_MAX_LENGTH ||
      comment2Draft.trim().length > COMMENT_MAX_LENGTH ||
      comment3Draft.trim().length > COMMENT_MAX_LENGTH
    ) {
      toast.error(t('admin.user.profile.validation.personal.max.length'));
      return;
    }

    const nextUniqueCode = canEditUniqueCode
      ? uniqueCodeDraft.trim()
      : (workspace.uniqueCode ?? '');
    if (canEditUniqueCode) {
      if (!nextUniqueCode) {
        toast.error(t('user.create.client.code.required'));
        return;
      }
      if (nextUniqueCode.length < 3) {
        toast.error(t('user.create.client.code.min'));
        return;
      }
      if (nextUniqueCode.length > 50 || !/^([a-zA-Z0-9]+)$/.test(nextUniqueCode)) {
        toast.error(t('form.fields.schema.describe.unique.code'));
        return;
      }
    }

    const selectedRole = programRoleDraft as Role;
    if (!assignableRolesForAboutModal.includes(selectedRole)) {
      toast.error(t('common.not.allowed'));
      return;
    }

    const nextWorkspaceStatus: UserStatus =
      workspace.status === 'user.status.inactive'
        ? 'user.status.inactive'
        : workspace.status === 'user.status.blocked'
          ? 'user.status.blocked'
          : programTermsAcceptedDraft
            ? 'user.status.registered'
            : 'user.status.unregistered';

    const payload: UserUserUpdateJsonMergePatchModel = {
      email: nextEmail,
      firstname: nextFirstname,
      lastname: nextLastname,
      userWorkspace: {
        uniqueCode: nextUniqueCode,
        companyUniqueCode: workspace.company?.uniqueCode ?? null,
        maxRole: programRoleDraft,
        status: nextWorkspaceStatus,
        phone: personalPhoneDraft.trim() || null,
        comment: commentDraft.trim() || null,
        comment2: comment2Draft.trim() || null,
        comment3: comment3Draft.trim() || null
      }
    };

    updateAboutMutation.mutate({ id: userId, data: payload });
  };

  const handleSubmitCompanyModal = () => {
    if (!user || !workspace) return;

    if (companySearch.trim() && !selectedCompanyOption) {
      toast.error(t('admin.user.profile.validation.company.select.from.list'));
      return;
    }
    const nextCompanyUniqueCode = selectedCompanyOption?.value ?? null;

    const payload: UserUserUpdateJsonMergePatchModel = {
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname,
      userWorkspace: {
        uniqueCode: workspace.uniqueCode,
        companyUniqueCode: nextCompanyUniqueCode,
        maxRole: workspace.maxRole as UserWorkspaceUserUpdateModelMaxRole,
        status: workspace.status,
        phone: workspace.phone ?? null,
        comment: workspace.comment ?? null,
        comment2: workspace.comment2 ?? null,
        comment3: workspace.comment3 ?? null
      }
    };

    updateCompanyMutation.mutate({ id: userId, data: payload });
  };

  const formattedCreatedAt = useMemo(
    () => (workspace?.createdAt ? format(workspace.createdAt, 'dd/MM/yyyy') : '-'),
    [workspace?.createdAt]
  );
  const formattedRegisteredAt = useMemo(
    () => (registeredAt ? format(registeredAt, 'dd/MM/yyyy') : '-'),
    [registeredAt]
  );
  const formattedLoginCountPerMonth = useMemo<FormattedDataForRendering[]>(() => {
    if (!workspace?.loginCountPerMonth) return [];
    return workspace.loginCountPerMonth.map((item) => {
      const monthName = format(new Date(item.year, item.month - 1), 'MMMM', { locale: fr });
      const formattedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      return {
        date: `${formattedMonthName} ${item.year}`,
        data: item.value,
        fill: getBarFillFromValue(item.value)
      };
    });
  }, [workspace?.loginCountPerMonth]);
  const companyResponsibles = useMemo(() => {
    return (companyDetailsQuery.data?.responsibles ?? [])
      .map((responsible) => {
        const responsibleUser = responsible.user;
        const fullname = responsibleUser?.fullname ?? '';

        if (!fullname) return null;

        return {
          id: responsibleUser?.id ? String(responsibleUser.id) : undefined,
          fullname,
          isDeleted: responsibleUser?.isDeleted
        };
      })
      .filter((responsible): responsible is NonNullable<typeof responsible> =>
        Boolean(responsible)
      );
  }, [companyDetailsQuery.data?.responsibles]);

  const handleConfirmToggleContactBlock = () =>
    toast(
      isBlocked
        ? t('admin.user.profile.confirm.unblock.contact')
        : t('admin.user.profile.confirm.block.contact'),
      {
        action: {
          label: t('label.confirm'),
          onClick: () => handleToggleContactBlock(!isBlocked)
        },
        cancel: {
          label: t('label.cancel'),
          onClick: () => {}
        }
      }
    );

  if (isLoading) {
    return (
      <div className="space-y-5">
        <h1 className="text-4xl font-bold">{t('admin.user.profile.title')}</h1>
        <UserSkeleton />
      </div>
    );
  }

  if (isSuccess && user) {
    return (
      <div className="space-y-5">
        <UserProfileHeader
          user={user}
          isBlocked={isBlocked}
          isRegistered={isRegistered}
          isInactive={isInactive}
          onToggleBlock={handleConfirmToggleContactBlock}
          blockPending={blockContactMutation.isPending}
          disableBlockAction={!workspace || currentUserWorkspace?.maxRole === 'ROLE_SALES_ADMIN'}
          blockDisabledTooltip={
            workspace && currentUserWorkspace?.maxRole === 'ROLE_SALES_ADMIN'
              ? t('admin.user.profile.action.block.contact.sales.tooltip')
              : undefined
          }
          backLabel={t('common.back')}
        />

        <UserDetailsGrid
          user={user}
          workspace={workspace}
          formattedCreatedAt={formattedCreatedAt}
          formattedRegisteredAt={formattedRegisteredAt}
          roleLabel={workspace?.maxRole ? t(roleToI18nKey(workspace.maxRole as Role)) : '-'}
          termsLabel={
            isAcceptedStatus(workspace?.status)
              ? t('admin.user.profile.terms.accepted')
              : t('admin.user.profile.terms.pending')
          }
          modalState={{
            isAboutOpen: isAboutModalOpen,
            isCompanyOpen: isCompanyModalOpen
          }}
          drafts={{
            firstname: firstnameDraft,
            lastname: lastnameDraft,
            personalEmail: personalEmailDraft,
            personalPhone: personalPhoneDraft,
            comment: commentDraft,
            comment2: comment2Draft,
            comment3: comment3Draft,
            uniqueCode: uniqueCodeDraft,
            programRole: programRoleDraft,
            programTermsAccepted: programTermsAcceptedDraft,
            companySearch,
            selectedCompanyOption
          }}
          assignableRoles={assignableRolesForAboutModal}
          canEditUniqueCode={canEditUniqueCode}
          isTermsReadOnly={isTermsReadOnly}
          loadingState={{
            updateAboutPending: updateAboutMutation.isPending,
            updateCompanyPending: updateCompanyMutation.isPending,
            companyLoading: companyQuery.isLoading,
            companyDetailsLoading: companyDetailsQuery.isLoading
          }}
          companyOptions={
            companyQuery.data?.data?.map((company: CompanyCompanyCollectionReadModel) => ({
              label: company.name,
              value: company.uniqueCode
            })) ?? []
          }
          companyId={companyId}
          companyResponsibles={companyResponsibles}
          handlers={{
            setIsAboutOpen: setIsAboutModalOpen,
            setIsCompanyOpen: setIsCompanyModalOpen,
            onFirstnameChange: setFirstnameDraft,
            onLastnameChange: setLastnameDraft,
            onPersonalEmailChange: setPersonalEmailDraft,
            onPersonalPhoneChange: setPersonalPhoneDraft,
            onCommentChange: setCommentDraft,
            onComment2Change: setComment2Draft,
            onComment3Change: setComment3Draft,
            onUniqueCodeChange: setUniqueCodeDraft,
            onProgramRoleChange: (value) =>
              setProgramRoleDraft(value as UserWorkspaceUserUpdateModelMaxRole),
            onProgramTermsChange: setProgramTermsAcceptedDraft,
            onCompanySearchChange: setCompanySearch,
            onCompanyOptionChange: setSelectedCompanyOption,
            onSubmitAboutModal: handleSubmitAboutModal,
            onSubmitCompanyModal: handleSubmitCompanyModal
          }}
          translateRole={(role) => t(roleToI18nKey(role as Role))}
          companySearchPlaceholder={t('common.label.search.by', { field: t('label.user.company') })}
        />

        <UserLoginStatsCard
          title={t('admin.user.login.stats.title')}
          loginCountTitle={t('admin.user.login.count.title')}
          chartInfo={t('admin.user.login.chart.info')}
          loginCount={workspace?.loginCount ?? 0}
          formattedLoginCountPerMonth={formattedLoginCountPerMonth}
          userId={userId}
          userUniqueCode={workspace?.uniqueCode ?? ''}
          onSendMagicLink={
            !isAdminRole(workspace?.maxRole as Role)
              ? () => sendMagicLinkMutation.mutate({ id: userId })
              : undefined
          }
          isSendingMagicLink={sendMagicLinkMutation.isPending}
        />

        <HistoryCard
          title={t('admin.emails.title')}
          description={t('admin.user.emails.description')}
        >
          <EmailHistoryTable
            key={workspace?.uniqueCode}
            source={{ type: 'user', uniqueCode: workspace?.uniqueCode ?? undefined }}
          />
        </HistoryCard>

        {hasHistory && (
          <HistoryCard
            title={t('admin.history.user.title')}
            description={t('admin.history.user.description')}
          >
            <UserHistoryTable userId={userId} registeredAt={registeredAt} />
          </HistoryCard>
        )}

        <div className="overflow-hidden rounded-xl border border-destructive/30 bg-destructive/5">
          <div className="flex gap-3 items-center px-6 py-4 bg-destructive">
            <AlertTriangle className="w-5 h-5 text-destructive-foreground shrink-0" />
            <h3 className="text-base font-semibold text-destructive-foreground">
              {t('admin.user.profile.danger.zone.title')}
            </h3>
          </div>
          <div className="flex flex-col gap-4 justify-between px-6 py-5 sm:flex-row sm:items-center">
            <p className="text-sm text-destructive/80">
              {t('admin.user.profile.danger.zone.description')}
            </p>
            <Button
              variant="destructive"
              rounded="full"
              className="shrink-0"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <Trash2 className="w-4 h-4" />
              {t('admin.user.profile.danger.zone.delete.button')}
            </Button>
          </div>
        </div>

        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader className="items-center text-center">
              <div className="flex justify-center items-center mb-2 w-14 h-14 rounded-full bg-destructive/10">
                <Trash2 className="w-6 h-6 text-destructive" />
              </div>
              <DialogTitle className="text-center">
                {t('admin.user.delete.modal.title')}
              </DialogTitle>
            </DialogHeader>

            <div className="py-1 space-y-3">
              {isLastManager ? (
                <div className="p-4 space-y-2 text-sm text-center rounded-lg border border-destructive bg-destructive/5">
                  <p className="font-semibold text-destructive">
                    {t('admin.user.delete.modal.last.manager.warning')}
                  </p>
                  <p className="text-muted-foreground">
                    {t('admin.user.delete.modal.last.manager.inactive.warning')}
                  </p>
                  <p className="text-muted-foreground">
                    {t('admin.user.delete.modal.last.manager.irreversible')}
                  </p>
                </div>
              ) : (
                <DialogDescription className="text-center">
                  {t('admin.user.delete.modal.description')}
                </DialogDescription>
              )}

              {hasSalesHierarchyDirectReports && (
                <p className="text-sm font-medium text-center text-muted-foreground">
                  {t('admin.user.delete.modal.sales.hierarchy.warning')}
                </p>
              )}
            </div>

            <DialogFooter className="flex-col-reverse gap-2 sm:flex-col sm:space-x-0">
              <Button
                variant="destructive"
                rounded="full"
                className="w-full"
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate({ id: userId })}
              >
                {t('admin.user.delete.modal.confirm')}
              </Button>
              <Button
                variant="outline"
                rounded="full"
                className="w-full"
                disabled={deleteMutation.isPending}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                {t('admin.user.delete.modal.cancel')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
};
